import { useRef, useState } from 'react';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Download } from '@ui/shared/icons';

/**
 * Export — settings-rewards.md §5/EX-1/EX-2/EX-3
 * (phase-2e-nav-settings-export.md). One click, no intermediate screen: a
 * single "Export everything" button calls `exportData()`, serializes the
 * result, and triggers a browser download — no confirmation step, since
 * export only reads (EX-3, the deliberate asymmetry with import).
 *
 * Filename uses TODAY's date (the export moment), not the data's own date
 * range max — a fixed product decision confirmed by the owner
 * (phase-2e-nav-settings-export.md, "Export filename date source").
 */
type Stage = 'idle' | 'exporting';

export function ExportSection() {
  const api = useDomainApi();
  const [stage, setStage] = useState<Stage>('idle');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  // A ref (not the `stage` state) is the actual reentrancy guard: two rapid
  // clicks can both run their synchronous event-handler bodies before React
  // commits the re-render that would make `disabled` take effect, so a
  // check against `stage` alone (read from a stale closure) is not
  // sufficient -- the ref is mutated synchronously on the very first click,
  // so the second click's guard check always sees the up-to-date value.
  // `stage` is kept purely to drive the disabled/label rendering.
  const inFlightRef = useRef(false);

  async function handleExport() {
    // In-flight guard (mirrors ImportFlow's own stage state machine, which
    // incidentally already protects its confirm button the same way) -- a
    // rapid double-click must call exportData()/trigger the download exactly
    // once, never twice.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStage('exporting');
    try {
      const file = await api.exportData();
      const filename = `compass-export-${todayIsoDate()}.json`;
      downloadJson(file, filename);
      setConfirmation(`Exported. ${file.events.length} events.`);
    } finally {
      setStage('idle');
      // The guard's reset is deferred by one macrotask (not cleared in this
      // same microtask turn) so a second click that lands immediately after
      // export resolves -- e.g. exportData() resolving in a single
      // microtask, faster than the gap between two real/simulated clicks --
      // still sees the guard engaged. Re-enabling stays effectively
      // instant to a real user (a macrotask is imperceptible) while closing
      // the exact race QA reproduced.
      setTimeout(() => {
        inFlightRef.current = false;
      }, 0);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void handleExport()}
        disabled={stage === 'exporting'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          background: stage === 'exporting' ? 'var(--surface-hover)' : 'var(--hue-primary-vivid)',
          border: '1px solid ' + (stage === 'exporting' ? 'var(--border-subtle)' : 'var(--hue-primary-strong)'),
          color: stage === 'exporting' ? 'var(--text-disabled)' : 'var(--text-on-accent)',
          fontWeight: 600,
          fontSize: 'var(--type-body-size)',
          cursor: stage === 'exporting' ? 'not-allowed' : 'pointer',
        }}
      >
        <Download size={16} aria-hidden="true" />
        {stage === 'exporting' ? 'Exporting…' : 'Export everything'}
      </button>
      <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        Everything, in one file. Yours. Works the same wherever the app is hosted.
      </p>
      {confirmation && (
        <p
          role="status"
          style={{
            marginTop: 'var(--space-2)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 500,
            color: 'var(--state-confirm)',
          }}
        >
          {confirmation}
        </p>
      )}
    </div>
  );
}

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
